import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useReferral } from '@/hooks/useReferral';
import { Gift, CheckCircle } from 'lucide-react';

interface ApplyReferralCodeProps {
  onSuccess?: () => void;
}

export const ApplyReferralCode = ({ onSuccess }: ApplyReferralCodeProps) => {
  const { applyReferralCode } = useReferral();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleApply = async () => {
    if (!code.trim()) return;

    setLoading(true);
    const result = await applyReferralCode(code.trim());
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      if (onSuccess) onSuccess();
    }
  };

  if (success) {
    return (
      <Card className="clean-card border-green-200 bg-green-50">
        <CardContent className="p-6 text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
          <h3 className="text-lg font-semibold text-green-900 mb-2">
            Referral Code Applied!
          </h3>
          <p className="text-sm text-green-700">
            Your referrer will earn 1% from your spending for the next 3 months.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="clean-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          Have a Referral Code?
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Enter a referral code to support a creator. They'll earn 1% from your spending for 3 months!
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="Enter code (e.g., ABC12345)"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={loading}
            className="rounded-xl font-mono"
            maxLength={8}
          />
          <Button 
            onClick={handleApply}
            disabled={!code.trim() || loading}
            className="rounded-full"
          >
            {loading ? 'Applying...' : 'Apply'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
